import axios from "axios";

const instance = axios.create({
  // AWS/EC2 production and local development both use the same-origin API.
  // Nginx proxies /api in production; CRA proxies it during local development.
  baseURL: "/api",
  timeout: 30000,
});

// Keep GET results for the lifetime of the current browser session. Mentor LMS
// routes unmount when users move between sections, so component-local state is
// otherwise lost and the same expensive requests are repeated on every Back.
// This cache is intentionally memory-only: no learner data is written to disk.
const responseCache = new Map();
const pendingGets = new Map();
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

const stableValue = (value) => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        if (value[key] !== undefined) result[key] = stableValue(value[key]);
        return result;
      }, {});
  }
  return value;
};

const getCacheKey = (url, config = {}) => {
  const userScope = localStorage.getItem("oeson_id") || "anonymous";
  return `${userScope}:${url}:${JSON.stringify(stableValue(config.params || {}))}`;
};

export const peekCachedGet = (url, config = {}, ttl = DEFAULT_CACHE_TTL) => {
  const entry = responseCache.get(getCacheKey(url, config));
  if (!entry || Date.now() - entry.cachedAt > ttl) return null;
  return entry.response;
};

export const cachedGet = (url, config = {}, options = {}) => {
  const { force = false, ttl = DEFAULT_CACHE_TTL } = options;
  const key = getCacheKey(url, config);
  const cached = responseCache.get(key);

  if (!force && cached && Date.now() - cached.cachedAt <= ttl) {
    return Promise.resolve(cached.response);
  }

  // If two mounted widgets need the same endpoint, let them share one request.
  if (!force && pendingGets.has(key)) return pendingGets.get(key);

  const request = instance
    .get(url, config)
    .then((response) => {
      responseCache.set(key, { response, cachedAt: Date.now() });
      return response;
    })
    .finally(() => pendingGets.delete(key));

  pendingGets.set(key, request);
  return request;
};

export const invalidateQueryCache = (urlPart) => {
  if (!urlPart) {
    responseCache.clear();
    pendingGets.clear();
    return;
  }
  responseCache.forEach((_value, key) => {
    if (key.includes(urlPart)) responseCache.delete(key);
  });
};

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem("oeson_auth");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

instance.interceptors.response.use(
  (response) => {
    const method = response.config?.method?.toLowerCase();
    // Mutations can affect counts and detail screens in several sections. A
    // conservative invalidation keeps cached navigation fast without serving
    // stale administrative data after an edit.
    if (method && method !== "get") invalidateQueryCache();
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem("oeson_auth")) {
      localStorage.removeItem("oeson_auth");
      localStorage.removeItem("oeson_role");
      localStorage.removeItem("oeson_roles");
      localStorage.removeItem("oeson_id");
      localStorage.removeItem("oeson_session_id");
      invalidateQueryCache();
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(error);
  },
);

export default instance;
